import { useState, useEffect, useRef, useCallback, useMemo } from "react";

interface ServiceNode {
  label: string;
  angle: number;
  icon: string;
}

interface Particle {
  id: number;
  size: number;
  left: number;
  delay: number;
  duration: number;
  drift: number;
}

const serviceNodes: ServiceNode[] = [
  { label: "Security Systems", angle: 0, icon: "ri-shield-check-line" },
  { label: "SaaS Platforms", angle: 60, icon: "ri-cloud-line" },
  { label: "Cloud Infra", angle: 120, icon: "ri-database-2-line" },
  { label: "Custom Dev", angle: 180, icon: "ri-code-s-slash-line" },
  { label: "AI & Automation", angle: 240, icon: "ri-brain-line" },
  { label: "Managed IT", angle: 300, icon: "ri-server-line" },
];

const CX = 210;
const CY = 262;
const RADIUS = 150;
const CENTER_R = 30;

function getNodeCoords(angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + RADIUS * Math.cos(rad),
    y: CY + RADIUS * Math.sin(rad),
  };
}

export default function HeroSection() {
  const heroRef = useRef<HTMLElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  const handleScroll = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  // ── Floating particles ──
  const particles = useMemo<Particle[]>(
    () =>
      Array.from({ length: 30 }, (_, i) => ({
        id: i,
        size: Math.random() * 3 + 1.5,
        left: Math.random() * 100,
        delay: Math.random() * 10,
        duration: Math.random() * 8 + 8,
        drift: (Math.random() - 0.5) * 60,
      })),
    [],
  );

  // ── Mouse parallax ──
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    setMousePos({ x, y });
  }, []);

  const handleMouseEnter = useCallback(() => setIsHovering(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setMousePos({ x: 0, y: 0 });
  }, []);

  // ── Reveal-on-load ──
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll(".reveal");
            reveals.forEach((r) => r.classList.add("visible"));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Scroll progress ──
  useEffect(() => {
    let rafId: number;

    const updateProgress = () => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const heroHeight = rect.height;
      const scrolledPast = Math.max(0, -rect.top);
      const progress = Math.min(1, scrolledPast / (heroHeight * 0.75));
      setScrollProgress(progress);
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateProgress);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    updateProgress();

    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const getNodeActivation = useCallback(
    (index: number): number => {
      const total = serviceNodes.length - 1;
      const threshold = index / total;
      const transition = 0.25;
      if (scrollProgress < threshold - transition) return 0.25;
      if (scrollProgress > threshold + transition) return 1;
      const t = (scrollProgress - (threshold - transition)) / (2 * transition);
      const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      return 0.25 + eased * 0.75;
    },
    [scrollProgress],
  );

  const nodeCoords = serviceNodes.map((n) => getNodeCoords(n.angle));

  const ringSegments: { x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let i = 0; i < nodeCoords.length; i++) {
    const a = nodeCoords[i];
    const b = nodeCoords[(i + 1) % nodeCoords.length];
    ringSegments.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y });
  }

  // Parallax offsets
  const parallaxX = mousePos.x * 12;
  const parallaxY = mousePos.y * 12;
  const bgParallaxX = mousePos.x * -20;
  const bgParallaxY = mousePos.y * -20;

  return (
    <section id="hero" ref={heroRef} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="relative min-h-screen flex items-center overflow-hidden bg-background-50">
      {/* ═══════════════ AMBIENT GRADIENT BLOOMS ═══════════════ */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 w-[800px] h-[800px] rounded-full bg-[radial-gradient(circle,oklch(var(--primary-100)/0.08),transparent_65%)] transition-transform duration-700 ease-out"
          style={{
            transform: `translate(calc(-50% + ${bgParallaxX}px), calc(-50% + ${bgParallaxY}px))`,
          }}
        />
        <div
          className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,oklch(var(--accent-100)/0.06),transparent_65%)] transition-transform duration-700 ease-out"
          style={{
            transform: `translate(${-bgParallaxX * 0.5}px, ${-bgParallaxY * 0.5}px)`,
          }}
        />
      </div>

      {/* ═══════════════ FLOATING PARTICLES ═══════════════ */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              width: `${p.size}px`,
              height: `${p.size}px`,
              left: `${p.left}%`,
              bottom: "-10px",
              opacity: 0,
              background: p.id % 3 === 0 ? `oklch(var(--accent-400) / 0.5)` : p.id % 3 === 1 ? `oklch(var(--primary-400) / 0.45)` : `oklch(var(--foreground-400) / 0.35)`,
              animation: `particleFloat ${p.duration}s ${p.delay}s linear infinite`,
              ["--drift" as string]: `${p.drift}px`,
            }}
          />
        ))}
      </div>

      {/* ═══════════════ MAIN CONTENT ═══════════════ */}
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-6 lg:px-12 pt-32 md:pt-40 pb-20">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16">
          {/* ── LEFT: TEXT ── */}
          <div className="flex-1 w-full lg:max-w-[55%]">
            {/* Badge */}
            <div className="reveal inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-100/80 border border-background-200/60 mb-8">
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full rounded-full bg-accent-500 opacity-75 animate-ping" />
                <span className="relative inline-flex w-2 h-2 rounded-full bg-accent-500" />
              </span>
              <span className="text-xs font-medium text-foreground-700 tracking-wide uppercase">Managed IT · System Security · Engineering</span>
            </div>

            {/* Headline — word-by-word animation */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-foreground-950 leading-[1.08] tracking-tight">
              <span className="reveal inline-block lg:text-6xl [animation:wordSlideLeft_0.8s_cubic-bezier(0.16,1,0.3,1)_forwards] opacity-0">Technology</span>
              <br />
              <span className="reveal inline-block lg:text-6xl [animation:wordSlideRight_0.8s_0.15s_cubic-bezier(0.16,1,0.3,1)_forwards] opacity-0" style={{ animationDelay: "0.15s" }}>
                Solutions
              </span>
              <br />
              <span className="reveal inline-block lg:text-6xl [animation:wordSlideUp_0.8s_0.3s_cubic-bezier(0.16,1,0.3,1)_forwards] opacity-0" style={{ animationDelay: "0.3s" }}>
                Built for
              </span>
              {"   "}
              <span className="relative inline-block">
                <span className="reveal relative z-10 lg:text-7xl inline-block text-primary-500 [animation:wordPopIn_0.7s_0.5s_cubic-bezier(0.34,1.56,0.64,1)_forwards] opacity-0 scale-75" style={{ animationDelay: "0.5s" }}>
                  Growth
                </span>
                <span
                  className="absolute bottom-1 left-0 h-3 bg-accent-500/25 -z-0 rounded-full [animation:growLine_0.6s_0.8s_cubic-bezier(0.16,1,0.3,1)_forwards]"
                  style={{
                    width: "0%",
                    animationDelay: "0.8s",
                    animationFillMode: "forwards",
                  }}
                />
              </span>
            </h1>

            {/* Description */}
            <p
              className="reveal reveal-delay-2 mt-6 max-w-xl text-base md:text-lg text-foreground-600 leading-relaxed opacity-0"
              style={{
                animation: "fadeInUp 0.8s 0.6s cubic-bezier(0.16,1,0.3,1) forwards",
              }}
            >
              From <strong className="font-semibold text-foreground-800">system security</strong> and managed IT to custom software, SaaS platforms, and business websites — we design, build, and protect every layer of your technology so you can
              operate efficiently and scale with confidence.
            </p>

            {/* CTAs */}
            <div
              className="reveal reveal-delay-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-8 opacity-0"
              style={{
                animation: "fadeInUp 0.8s 0.8s cubic-bezier(0.16,1,0.3,1) forwards",
              }}
            >
              <span className="relative inline-flex group">
                <span className="absolute inset-0 rounded-full bg-foreground-950/30 dark:bg-foreground-50/20 animate-pulseGlow" />
                <button
                  onClick={() => handleScroll("#cta")}
                  className="relative px-7 py-3.5 rounded-full bg-foreground-950 text-background-50 dark:text-foreground-950 dark:bg-foreground-50 text-sm font-semibold hover:bg-foreground-800 dark:hover:bg-foreground-200 transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-[1.03] active:scale-[0.98]"
                >
                  Schedule a Free Consultation
                </button>
              </span>
              <button
                onClick={() => handleScroll("#solutions")}
                className="px-7 py-3.5 rounded-full text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer underline underline-offset-4 decoration-foreground-300 hover:decoration-foreground-800 hover:scale-[1.03]"
              >
                View Services
              </button>
            </div>

            {/* Trusted-by mini strip */}
            <div
              className="reveal reveal-delay-4 mt-10 flex items-center gap-6 opacity-0"
              style={{
                animation: "fadeInUp 0.8s 1s cubic-bezier(0.16,1,0.3,1) forwards",
              }}
            >
              <span className="text-xs font-medium text-foreground-500 whitespace-nowrap tracking-wide">Trusted by 20+ companies</span>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center" />
                ))}
                <span className="text-xs font-semibold text-foreground-700 ml-1">5.0</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT: CONNECTED NODE NETWORK ── */}
          <div
            className="reveal reveal-delay-4 flex-1 w-full lg:max-w-[45%] hidden lg:flex items-center justify-center"
            style={{
              transform: isHovering ? `translate(${parallaxX}px, ${parallaxY}px)` : "translate(0, 0)",
              transition: isHovering ? "transform 0.15s ease-out" : "transform 0.8s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <div className="relative w-full max-w-[420px] aspect-[4/5]">
              {/* ═══════════════ SVG: grid, connections, data flow, center core ═══════════════ */}
              <svg viewBox="0 0 420 525" className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="dotGrid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
                    <circle cx="14" cy="14" r="1" fill="oklch(var(--foreground-400) / 0.28)" />
                  </pattern>

                  <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>

                  <filter id="strongGlow" x="-80%" y="-80%" width="260%" height="260%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>

                  <radialGradient id="centerGrad" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="oklch(var(--primary-400) / 0.25)" />
                    <stop offset="100%" stopColor="oklch(var(--primary-400) / 0)" />
                  </radialGradient>
                </defs>

                {/* Dot grid background */}
                <rect x="0" y="0" width="420" height="525" fill="url(#dotGrid)" opacity="0.7" />

                {/* Center node aura (pulsing) */}
                <circle cx={CX} cy={CY} r={CENTER_R + 25} fill="url(#centerGrad)" className="[animation:centerPulse_3s_ease-in-out_infinite]" />

                {/* Ring connections (outer hexagon) */}
                {ringSegments.map((seg, i) => (
                  <g key={`ring-${i}`}>
                    <line x1={seg.x1} y1={seg.y1} x2={seg.x2} y2={seg.y2} stroke="oklch(var(--foreground-400) / 0.35)" strokeWidth="1.2" />
                    <line
                      x1={seg.x1}
                      y1={seg.y1}
                      x2={seg.x2}
                      y2={seg.y2}
                      stroke="oklch(var(--primary-300) / 0.5)"
                      strokeWidth="1.2"
                      strokeDasharray="3 18"
                      className="[animation:ringFlow_4s_linear_infinite]"
                      style={{ animationDelay: `${i * 0.6}s` }}
                    />
                  </g>
                ))}

                {/* Radial connections */}
                {nodeCoords.map((node, i) => (
                  <g key={`radial-${i}`}>
                    <line x1={CX} y1={CY} x2={node.x} y2={node.y} stroke="oklch(var(--foreground-400) / 0.28)" strokeWidth="1.2" />
                    <line
                      x1={CX}
                      y1={CY}
                      x2={node.x}
                      y2={node.y}
                      stroke="oklch(var(--primary-400) / 0.55)"
                      strokeWidth="1.4"
                      strokeDasharray="3 22"
                      className="[animation:dataFlowOutward_3s_linear_infinite]"
                      style={{ animationDelay: `${i * 0.4}s` }}
                    />
                    <line
                      x1={node.x}
                      y1={node.y}
                      x2={CX}
                      y2={CY}
                      stroke="oklch(var(--accent-300) / 0.4)"
                      strokeWidth="1"
                      strokeDasharray="2 26"
                      className="[animation:dataFlowInward_3.5s_linear_infinite]"
                      style={{ animationDelay: `${i * 0.4 + 1.5}s` }}
                    />
                  </g>
                ))}

                {/* Central node */}
                <circle cx={CX} cy={CY} r={CENTER_R + 4} fill="oklch(var(--primary-500))" filter="url(#strongGlow)" />

                {/* Orbiting particles */}
                {[0, 1, 2].map((idx) => (
                  <circle
                    key={`orbit-${idx}`}
                    cx={CX}
                    cy={CY}
                    r={1.8}
                    fill={idx === 0 ? "oklch(var(--accent-400) / 0.8)" : idx === 1 ? "oklch(var(--primary-300) / 0.7)" : "oklch(var(--accent-500) / 0.6)"}
                    className="[animation:orbitParticle_6s_linear_infinite]"
                    style={{
                      transformOrigin: `${CX}px ${CY}px`,
                      animationDelay: `${idx * 2}s`,
                      animationDuration: `${5 + idx * 1.5}s`,
                    }}
                  />
                ))}

                {/* Free-floating data dots */}
                {[
                  { x: 90, y: 140, d: 2.5, t: 8 },
                  { x: 330, y: 380, d: 2, t: 6.5 },
                  { x: 160, y: 400, d: 1.8, t: 7 },
                  { x: 300, y: 170, d: 2.2, t: 9 },
                ].map((dot, i) => (
                  <circle
                    key={`float-${i}`}
                    cx={dot.x}
                    cy={dot.y}
                    r={dot.d}
                    fill="oklch(var(--primary-300) / 0.5)"
                    className="[animation:dataDotFloat_4s_ease-in-out_infinite]"
                    style={{
                      animationDelay: `${i * 1.2}s`,
                      animationDuration: `${dot.t}s`,
                    }}
                  />
                ))}
              </svg>

              {/* ═══════════════ HTML: center icon + outer service nodes ═══════════════ */}

              {/* Center node: building icon + label */}
              <div
                className="absolute flex flex-col items-center select-none pointer-events-none"
                style={{
                  left: `${(CX / 420) * 100}%`,
                  top: `${(CY / 480) * 100}%`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <i className="ri-building-2-line text-lg text-background-50 w-6 h-6 flex items-center justify-center" />
                <span className="text-[16px] font-bold whitespace-nowrap mt-8 text-foreground-800 tracking-wide">Your Business</span>
              </div>

              {/* Outer service nodes */}
              {nodeCoords.map((node, i) => {
                const activation = getNodeActivation(i);
                const isActive = activation > 0;
                const svc = serviceNodes[i];

                return (
                  <div
                    key={svc.label}
                    className="absolute flex flex-col items-center transition-all duration-500 select-none"
                    style={{
                      left: `${(node.x / 420) * 100}%`,
                      top: `${(node.y / 525) * 100}%`,
                      transform: "translate(-50%, -50%)",
                      opacity: 0.75 + activation * 0.25,
                    }}
                  >
                    {/* Glow aura */}
                    <div
                      className="absolute rounded-full transition-all duration-500"
                      style={{
                        width: "56px",
                        height: "56px",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        background: `radial-gradient(circle, oklch(var(--primary-400) / ${0.12 + activation * 0.16}), transparent 70%)`,
                      }}
                    />

                    {/* Node circle */}
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center  bg-background-50 dark:bg-background-200 transition-all duration-500"
                      style={{
                        borderWidth: "2px",
                        borderStyle: "solid",
                        borderColor: isActive ? `oklch(var(--primary-400) / ${0.4 + activation * 0.35})` : "oklch(var(--foreground-500) / 0.55)",
                        boxShadow: isActive ? `0 0 ${12 + activation * 12}px oklch(var(--primary-400) / ${0.1 + activation * 0.12})` : "0 0 6px oklch(var(--foreground-300) / 0.06)",
                      }}
                    >
                      <i
                        className={`${svc.icon} text-base transition-all duration-500 w-5 h-5 flex items-center justify-center`}
                        style={{
                          color: isActive ? `oklch(var(--primary-500))` : `oklch(var(--foreground-700))`,
                        }}
                      />
                    </div>

                    {/* Label */}
                    <span
                      className="text-[11px] font-semibold whitespace-nowrap mt-2  transition-all duration-500"
                      style={{
                        color: isActive ? `oklch(var(--foreground-900))` : `oklch(var(--foreground-700))`,
                      }}
                    >
                      {svc.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════ SCROLL INDICATOR ═══════════════ */}
      <div className="reveal reveal-delay-5 absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none">
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-foreground-500 dark:text-foreground-400 font-medium tracking-widest uppercase">Scroll</span>

          <div className="w-5 h-8 rounded-full border-2 border-foreground-400 dark:border-foreground-500 flex items-start justify-center p-1">
            <div className="w-1.5 h-2 rounded-full bg-foreground-500 dark:bg-foreground-600 animate-bounce" />
          </div>
        </div>
      </div>

      {/* ═══════════════ KEYFRAMES ═══════════════ */}
      <style>{`
        @keyframes dataFlowOutward { to { stroke-dashoffset: -50; } }
        @keyframes dataFlowInward { to { stroke-dashoffset: 56; } }
        @keyframes ringFlow { to { stroke-dashoffset: -42; } }
        @keyframes orbitParticle {
          0% { transform: rotate(0deg) translateX(108px); }
          100% { transform: rotate(360deg) translateX(108px); }
        }
        @keyframes centerPulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.08); }
        }
        @keyframes particleFloat {
          0% { transform: translateY(0) translateX(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.3; }
          100% { transform: translateY(-110vh) translateX(var(--drift)); opacity: 0; }
        }
        @keyframes pulseGlow {
          0%, 100% { transform: scale(1); opacity: 0.4; }
          50% { transform: scale(1.08); opacity: 0.15; }
        }
        @keyframes wordSlideLeft {
          0% { opacity: 0; transform: translateX(-50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes wordSlideRight {
          0% { opacity: 0; transform: translateX(50px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes wordSlideUp {
          0% { opacity: 0; transform: translateY(30px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes wordPopIn {
          0% { opacity: 0; transform: scale(0.75); }
          60% { transform: scale(1.08); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes growLine {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(24px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes dataDotFloat {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
          50% { transform: translateY(-14px) scale(1.3); opacity: 0.85; }
        }
      `}</style>
    </section>
  );
}
